/**
 * Numbers ⇄ Hebrew letter numerals (gematria-style counting).
 *
 * Rules implemented:
 *
 * - Each letter has a fixed value: א–ט are 1–9, י–צ are 10–90, ק ר ש ת are 100–400. A number
 *   is the sum of its letters, written from the largest to the smallest.
 * - 500–900 are written with several letters: תק (500), תר (600), תש (700), תת (800), תתק
 *   (900). The final letters ך ם ן ף ץ stand for 500–900 in a medieval variant and are
 *   accepted when reading.
 * - 15 and 16 are written ט״ו and ט״ז (9+6, 9+7), never י״ה and י״ו, because those are
 *   forms of the divine name. The standard setting follows this; turn it off for the literal sum.
 * - Punctuation: a single letter takes a geresh after it (א׳), and a longer numeral takes a
 *   gershayim before its last letter (תשפ״ו). ASCII ' and " are accepted and can be written.
 * - Thousands are a letter with a geresh before the rest: 5786 is ה׳תשפ״ו. In the Hebrew
 *   calendar the thousands are usually left out (the "short count"): תשפ״ו.
 * - There is no letter for zero, so numerals start at 1 (א׳). This tool covers 1–9999.
 */

export const MAX_HEBREW = 9999;

const GERESH = "׳";
const GERSHAYIM = "״";

export interface HebrewOptions {
  /** Write 15 and 16 as ט״ו and ט״ז. */
  avoidDivineNames: boolean;
  /** Use ASCII ' and " instead of the Hebrew punctuation marks. */
  ascii: boolean;
  /** Leave out the thousands (the Hebrew calendar's short count). */
  shortCount: boolean;
}

export interface HebrewPart {
  letter: string;
  value: number;
}

export interface HebrewNumeral {
  text: string;
  /** The letters and what each is worth, thousands first. */
  parts: HebrewPart[];
}

const LETTER_VALUES: readonly (readonly [string, number])[] = [
  ["ת", 400],
  ["ש", 300],
  ["ר", 200],
  ["ק", 100],
  ["צ", 90],
  ["פ", 80],
  ["ע", 70],
  ["ס", 60],
  ["נ", 50],
  ["מ", 40],
  ["ל", 30],
  ["כ", 20],
  ["י", 10],
  ["ט", 9],
  ["ח", 8],
  ["ז", 7],
  ["ו", 6],
  ["ה", 5],
  ["ד", 4],
  ["ג", 3],
  ["ב", 2],
  ["א", 1],
];

/** Final letters used for 500–900 in the medieval variant (read only). */
const FINAL_VALUES: Record<string, number> = {
  ך: 500,
  ם: 600,
  ן: 700,
  ף: 800,
  ץ: 900,
};

const VALUE_BY_LETTER: Record<string, number> = {
  ...Object.fromEntries(LETTER_VALUES),
  ...FINAL_VALUES,
};

const LETTER_BY_VALUE: Record<number, string> = Object.fromEntries(
  LETTER_VALUES.map(([letter, value]) => [value, letter])
);

/** The letters for 1–999, largest first. */
function lettersFor(n: number, avoidDivineNames: boolean): HebrewPart[] {
  const parts: HebrewPart[] = [];
  let rest = n;
  const push = (value: number) => {
    parts.push({ letter: LETTER_BY_VALUE[value], value });
    rest -= value;
  };
  while (rest >= 400) {
    push(400);
  }
  if (rest >= 100) {
    push(Math.floor(rest / 100) * 100);
  }
  if (avoidDivineNames && (rest === 15 || rest === 16)) {
    push(9);
    push(rest);
    return parts;
  }
  if (rest >= 10) {
    push(Math.floor(rest / 10) * 10);
  }
  if (rest > 0) {
    push(rest);
  }
  return parts;
}

function punctuate(parts: HebrewPart[], ascii: boolean): string {
  const geresh = ascii ? "'" : GERESH;
  const gershayim = ascii ? '"' : GERSHAYIM;
  const letters = parts.map((p) => p.letter);
  if (letters.length === 1) {
    return letters[0] + geresh;
  }
  return letters.slice(0, -1).join("") + gershayim + letters.at(-1);
}

/**
 * 1000–9000 exactly. A bare "א׳" would also be 1, so these are written with the word for
 * thousand: אלף, אלפיים, ג׳ אלפים … ט׳ אלפים.
 */
function roundThousands(thousands: number, geresh: string): HebrewNumeral {
  const letter = LETTER_BY_VALUE[thousands];
  const parts = [{ letter, value: thousands * 1000 }];
  if (thousands === 1) {
    return { text: "אלף", parts };
  }
  if (thousands === 2) {
    return { text: "אלפיים", parts };
  }
  return { text: `${letter}${geresh} אלפים`, parts };
}

/** A whole number from 1 to 9999 as a Hebrew letter numeral, or null outside that range. */
export function hebrewNumeral(
  n: number,
  options: HebrewOptions
): HebrewNumeral | null {
  if (!Number.isInteger(n) || n < 1 || n > MAX_HEBREW) {
    return null;
  }
  const thousands = Math.floor(n / 1000);
  const rest = n % 1000;
  const parts: HebrewPart[] = [];
  let text = "";
  const geresh = options.ascii ? "'" : GERESH;
  if (thousands > 0 && rest === 0) {
    return roundThousands(thousands, geresh);
  }
  const keepThousands = thousands > 0 && !options.shortCount;
  if (keepThousands) {
    parts.push({ letter: LETTER_BY_VALUE[thousands], value: thousands * 1000 });
    text += LETTER_BY_VALUE[thousands] + geresh;
  }
  if (rest > 0) {
    const restParts = lettersFor(rest, options.avoidDivineNames);
    parts.push(...restParts);
    text += punctuate(restParts, options.ascii);
  }
  return { text, parts };
}

export type HebrewReadError = "empty" | "invalid" | "order" | "tooBig";
export type HebrewReadNote = "thousands" | "finalLetters" | "divineName";

export type HebrewReadResult =
  | { ok: true; value: number; notes: HebrewReadNote[] }
  | { ok: false; error: HebrewReadError };

const QUOTES_GERESH = /['’′׳]/g;
const QUOTES_GERSHAYIM = /["”″״]/g;
const SPACES = /[\s.]/g;
/** Marks inserted around letters; stripped before summing. */
const MARK = /[׳״]/g;
const ROUND_THOUSANDS = /^([ג-ט])׳?אלפים$/;

/** The value of one numeral (no thousands), or an error. */
function sumLetters(letters: string): number | HebrewReadError {
  let total = 0;
  let previous = Number.POSITIVE_INFINITY;
  let taws = 0;
  for (const letter of letters) {
    const value = VALUE_BY_LETTER[letter];
    if (value === undefined) {
      return "invalid";
    }
    if (value > previous) {
      return "order";
    }
    if (value === previous && letter !== "ת") {
      return "order";
    }
    taws += letter === "ת" ? 1 : 0;
    if (taws > 2) {
      return "order";
    }
    previous = value;
    total += value;
  }
  return total === 0 ? "invalid" : total;
}

function hasFinalLetters(letters: string): boolean {
  return [...letters].some((c) => c in FINAL_VALUES);
}

/** אלף, אלפיים and ג׳ אלפים … ט׳ אלפים: exact thousands, or null. */
function readRoundThousands(cleaned: string): number | null {
  if (cleaned === "אלף") {
    return 1000;
  }
  if (cleaned === "אלפיים") {
    return 2000;
  }
  const match = ROUND_THOUSANDS.exec(cleaned);
  return match === null ? null : (VALUE_BY_LETTER[match[1]] ?? 0) * 1000;
}

/** Read a Hebrew letter numeral (with or without its punctuation) into a number. */
export function readHebrewNumeral(text: string): HebrewReadResult {
  const cleaned = text
    .replace(SPACES, "")
    .replace(QUOTES_GERESH, GERESH)
    .replace(QUOTES_GERSHAYIM, GERSHAYIM);
  if (cleaned === "") {
    return { ok: false, error: "empty" };
  }
  const round = readRoundThousands(cleaned);
  if (round !== null) {
    return { ok: true, value: round, notes: ["thousands"] };
  }
  const inner = cleaned.slice(0, -1);
  const split = inner.split(GERESH);
  if (split.length > 2) {
    return { ok: false, error: "invalid" };
  }
  const notes: HebrewReadNote[] = [];
  let thousands = 0;
  let restText = cleaned;
  if (split.length === 2) {
    const head = split[0].replace(MARK, "");
    const multiplier = sumLetters(head);
    if (typeof multiplier === "string") {
      return { ok: false, error: multiplier };
    }
    if (multiplier > 9) {
      return { ok: false, error: "tooBig" };
    }
    thousands = multiplier * 1000;
    restText = cleaned.slice(split[0].length + 1);
    notes.push("thousands");
  }
  const letters = restText.replace(MARK, "");
  const rest = letters === "" ? 0 : sumLetters(letters);
  if (typeof rest === "string") {
    return { ok: false, error: rest };
  }
  if (hasFinalLetters(letters + split[0])) {
    notes.push("finalLetters");
  }
  if (letters === "יה" || letters === "יו") {
    notes.push("divineName");
  }
  return { ok: true, value: thousands + rest, notes };
}

/** A numeral's letters without any punctuation, for comparing spellings. */
export function stripHebrewMarks(text: string): string {
  return text
    .replace(SPACES, "")
    .replace(QUOTES_GERESH, "")
    .replace(QUOTES_GERSHAYIM, "")
    .replace(MARK, "");
}
